import React, { Component, createRef } from 'react'
import Dropzone from 'react-dropzone'

import Icon from './uiComponents/Icon'
import LoadingDucsDots from './uiComponents/LoadingDots/LoadingDots'
import ConfirmModal from './uiComponents/ConfirmModal/ConfirmModal'
import UploadModal from './uiComponents/UploadModal/UploadModal'

import { formatData } from './utils/formatData'
import capitalize from './utils/capitalize'
import ClickListener from './utils/useClickOutside'
import { getMimeType } from './utils/getMimeType'
import { convertProductPropertiesFormIntoArray } from "./utils/convertProductPropertiesFormIntoArray"
import {filesValidation, getRepositoryFiles, isValueValidJson} from "./utils/fileUploadUtils"
import {runByFeature} from './utils/runByFeature';

const formatType = (options, format, defType) => {
  return options ? options.map((type) => `${format}${type.toLowerCase()}`) : defType
}
const checkIncludesFormat = (a, b) => a.some((item) => b.includes(item))

class FileUpload extends Component {
  constructor (props) {
    super(props)

    this.state = {
      uploadedFiles: [],
      uploadFilesInProgress: [],
      validationMessages: [],
      validationMessagesType: '',
      repoFiles: [],
      filteredRepos: [],
      filesSelectedFromRepo: [],
      filesSelectedFromRepoForForm: [],
      breadcrumbData: ['root'],
      selectedRepo: {},
      dataToFilter: {},
      isOpenConfirm: false,
      isOpenUploadModal: false,
      fileToDelete: null,
      downloadedPreflight: '',
      uploadedFilesString: ''
    }
    const fileType = formatType(this.props.options.custom.allowedExtensions, '*.', '*.*')
    this.fileSupport = fileType.length >= 0 ? fileType.join(', ') : fileType
    this.isRepo = this.props.options.custom.enableExternalRepositoryUpload
    this.isLocal = this.props.options.custom.enableLocalFileUpload
    this.fileNameArr = this.props.options.custom.templateUrl && this.props.options.custom.templateUrl.split('=') || []
    this.features = this.props.features
  }

  valueParamUpdated(noOnchange) {
    const { value, schema, formContext } = this.props

    if(value) {
      if(schema.custom.usedAsProductionOutput && value.includes('.pdf')) {
        const { currentProduct } = formContext?.UStoreProvider?.state.customState.get()

        currentProduct && formContext?.UStoreProvider?.state.customState.set('currentProduct', {...currentProduct, Proof: {...currentProduct.Proof, MimeType: 'application/pdf'}})
      }
      runByFeature(this.features, {
        newStaticProduct: () => {
          if (isValueValidJson(value)) {
            const valueArr = JSON.parse(value)
            const files = valueArr.reduce((r, item) => {
              const extension = item.FileName.split('.')[1]
              return {...r, [item.FileName]: new File([item.FileName], item.FileName, {size: item.Size, type: getMimeType(extension)})}
            },{})

            if (!noOnchange) { this.props.onChange(value) }

            this.state.uploadedFiles.forEach(file => {
              if (files[file.name]) { delete files[file.name] }
            })

            this.setState({
              uploadedFiles: this.state.uploadedFiles.concat(Object.values(files)),
              validationMessages: [],
              validationMessagesType: ''
            })
          }
        },
        otherwise: () => {
          const valueArr = JSON.parse(value)
          const files = valueArr.reduce((r, item) => {
            const extension = item.FileName.split('.')[1]
            return {...r, [item.FileName]: new File([item.FileName], item.FileName, {size: item.Size, type: getMimeType(extension)})}
          },{})

          if (!noOnchange) { this.props.onChange(value) }

          this.state.uploadedFiles.forEach(file => {
            if (files[file.name]) { delete files[file.name] }
          })

          this.setState({
            uploadedFiles: this.state.uploadedFiles.concat(Object.values(files)),
            validationMessages: [],
            validationMessagesType: ''
          })
        }
      })
    }
  }

  componentDidMount() {
    this.valueParamUpdated()

    document.body.style.overflow = 'hidden auto';
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.value !== this.props.value) {
      this.valueParamUpdated(true)
    }

    if(prevState.isOpenUploadModal !== this.state.isOpenUploadModal) {
      this.state.isOpenUploadModal ? document.body.style.overflow = 'clip' : document.body.style.overflow = 'hidden auto'
    }

    runByFeature(this.features, {
      newStaticProduct: () => null,
      otherwise: () => {
        if(prevState.uploadFilesInProgress !== this.state.uploadFilesInProgress) {
          if (this.state.uploadFilesInProgress.length > 0 && !this.state.uploadedFilesString) {
            this.props.onChange('__error')
          }
        }
      }
    })
    const linkFromPreflightError = document.getElementById('preflightLink')
    if(linkFromPreflightError) {
      linkFromPreflightError.addEventListener('click', this.confirmationOfUpload(linkFromPreflightError.href))
    }
  }

  // upload image on drop or onClick upload file button
  validate = (onClick, acceptedFiles, type) => (e) => {
    const { schema, t } = this.props
    const { isOpenUploadModal } = this.state

    const validationErrors = filesValidation(acceptedFiles, 'click', schema, t, isOpenUploadModal)
      if (validationErrors.length <= 0) {
        this.setState({
          validationMessages: [],
          validationMessagesType: ''
        })
        if (type === 'local') {
          return
        }
        onClick(e)
      } else {
        if (type === 'local') {
          e.preventDefault()
        }
        this.setState({
          validationMessages: validationErrors,
          validationMessagesType: 'custom'
        })
      }
  }

  onDrop = (acceptedFiles) => {
    const { uploadedFiles, uploadFilesInProgress, isOpenUploadModal } = this.state
    const { formContext, id, t, schema } = this.props

    const { UStoreProvider } = formContext
    const { customState } = UStoreProvider.state.get()
    const { currentOrderItem } = customState

    const splittedIdArr = id.split('_')
    const splittedId = splittedIdArr[splittedIdArr.length - 1]

    const mappedAcc = acceptedFiles.map((file) => file)

    const existFiles = uploadedFiles.filter(({ name: id1 }) => mappedAcc.some(({ path: id2 }) => id2 === id1));

    if(existFiles.length > 0) {
      const errors = existFiles.map((file) => t('ducs.file_already_exists', {FileName: file.name}))
      this.setState({
        validationMessages: errors,
        validationMessagesType: 'custom'
      })
    } else {
      const filesInUploadProgress = uploadFilesInProgress.concat(mappedAcc)

      const validationErrors = filesValidation(uploadedFiles.concat(mappedAcc), 'drop', schema, t, isOpenUploadModal)
      const validationErrorsInProgress = filesValidation(filesInUploadProgress, 'drop', schema, t, isOpenUploadModal)

      if (validationErrors.length === 0 && validationErrorsInProgress.length === 0) {
        if(filesInUploadProgress.length > 0) {
          setTimeout(() => {
            this.setState({
              validationMessages: [],
              validationMessagesType: '',
              uploadFilesInProgress: filesInUploadProgress
            })
          })
        }

        UStoreProvider.api.orders.uploadFile(currentOrderItem.ID, splittedId, mappedAcc)
          .then((res) => {
            let state = {}

            if (!res[0]) {
              runByFeature(this.features, {
                fileUploadFix: () => {},
                otherwise: () =>  this.props.onChange('__xmpie__clear__', [])
              })
              state = {
                validationMessages: [t('ducs.something_wrong_try_again')],
                validationMessagesType: 'custom',
                uploadFilesInProgress: []
              }
            } else {
              runByFeature(this.features, {
                newStaticProduct: () => {
                  if (res[0].FileInfoList) {
                    this.props.onChange(res[0].FileInfoList)
                    state = {
                      uploadedFiles: uploadedFiles.concat(mappedAcc),
                      uploadedFilesString: res[0].Value,
                      validationMessages: [],
                      uploadFilesInProgress: [],
                      validationMessagesType: ''
                    }
                  }
                },
                otherwise: () => {
                  if (res[0].Value) {
                    this.props.onChange(res[0].Value)
                    state = {
                      uploadedFiles: uploadedFiles.concat(mappedAcc),
                      uploadedFilesString: res[0].Value,
                      validationMessages: [],
                      uploadFilesInProgress: [],
                      validationMessagesType: ''
                    }
                  }
                }
              })
              if (res[0].Message) {
                runByFeature(this.features, {
                  fileUploadFix: () => {},
                  otherwise: () =>  this.props.onChange('__xmpie_error__')
                })
                state = {
                  validationMessages: [res[0].Message],
                  uploadFilesInProgress: [],
                  validationMessagesType: res[0].TypeName
                }
              }
            }
            this.setState({...state})
          })
          .catch((err) => console.error('files err', err))
      } else {
        this.setState({
          validationMessages: validationErrors,
          validationMessagesType: 'custom'
        })
      }
    }
  }

  selectRepo = (e) => {
    const { filteredRepos } = this.state
    const repo = filteredRepos.find((repo) => repo.ID === e.target.id)

    this.setState({
      selectedRepo: repo,
      dataToFilter: repo,
      breadcrumbData: ['root']
    })
  }

  handleCloseConfirmModal = () => {this.setState({ isOpenConfirm: false })}

  // delete file
  handleConfirmModal = () => {
    const { formContext, id } = this.props
    const { fileToDelete, uploadedFiles } = this.state

    const { UStoreProvider } = formContext
    const { customState } = UStoreProvider.state.get()
    const { currentOrderItem } = customState

    const splittedIdArr = id.split('_')
    const splittedId = splittedIdArr[splittedIdArr.length - 1]

    const fileURIToDelete = `&fileNames=${encodeURIComponent(fileToDelete)}`
    UStoreProvider.api.orders.deleteFiles(currentOrderItem.ID, splittedId, fileURIToDelete)
      .then((res) => {
        const filesFromResponse = res.Value.split(';')
        const filteredKeywords = uploadedFiles.filter((word) => filesFromResponse.includes(word.name));

        runByFeature(this.features, {
          newStaticProduct: () => {
            this.props.onChange(res.FileInfoList)
          },
          otherwise: () => {
            this.props.onChange(res.Value)
          }
        })
        this.props.onChange(res.FileInfoList)
        this.setState({ uploadedFiles: filteredKeywords })
      })
      .catch((err) => console.log('files err', err))
    this.handleCloseConfirmModal()
  }

  onDeleteFile = (file) => () => {
    this.setState({
      isOpenConfirm: true,
      fileToDelete: file
    })
  }

  updateState = (value) => {
    let repos
    let filteredRepos
    const { options } = this.props
    const loweredSupportedFiles = options.custom.allowedExtensions.map((item) => item.toLowerCase())
    if(loweredSupportedFiles.length > 0) {
      const filteredReposByFormats = value.reduce((acc, repo) => {
        const formats = repo.Files.map((file) => file.Path.split('.')[file.Path.split('.').length - 1])
        acc = checkIncludesFormat(loweredSupportedFiles, formats)
          ? acc.concat({ ...repo, Files: repo.Files.filter((r) => loweredSupportedFiles.includes(r.Path.split('.')[r.Path.split('.').length -1])) }) : acc
        return acc
      }, [])

      filteredRepos = filteredReposByFormats.map((repo) => ({ ID: repo.ID, Name: repo.Name, Files: formatData(repo.Files) }))
      repos = filteredReposByFormats

    } else {
      filteredRepos = value.map((repo) => ({ ID: repo.ID, Name: repo.Name, Files: formatData(repo.Files) }))
      repos = value
    }

    this.setState({
      repoFiles: repos,
      selectedRepo: filteredRepos[0],
      filteredRepos: filteredRepos,
      dataToFilter: filteredRepos[0]
    })
  }

  getRpoFiles = () => {
    const { options, formContext } = this.props
    const { selectedRepo } = this.state

    if (Object.keys(selectedRepo).length <= 0) {
      const promises = getRepositoryFiles(this.isRepo, options, formContext)
      Promise.all(promises).then((v) => {this.updateState(v.filter(i => !!i && i))})
    }
    this.setState({isOpenUploadModal: true})
  }

  handleCloseUploadModal = () => {this.setState({isOpenUploadModal: false})}

  onSelectFile = (e) => {
    const { selectedRepo, uploadedFiles, isOpenUploadModal } = this.state
    const {schema, t} = this.props

    const name = e.target.dataset.name
    const fullPath = e.target.dataset.fullpath

    if (selectedRepo && Object.keys(selectedRepo).length) {
      const updatedFiles = Object.keys(selectedRepo.Files).reduce((acc, key) => {
        const filesArray = selectedRepo.Files[key].map((file) => file.Path === name && fullPath === file.fullPath ? {...file, selected: !file.selected} : file)
        return { ...acc, [key]: filesArray }
      }, {})

      const selectedFromRepoToForm = Object.keys(updatedFiles).reduce((acc, next) => {
        updatedFiles[next].forEach((item) => {
           acc = item.selected ? acc.concat(`${selectedRepo.ID}:${item.originalPath}`) : acc
        })
        return acc
      }, [])

      const selectedFromRepo = Object.keys(updatedFiles).reduce((acc, next) => {
        updatedFiles[next].forEach((item) => {
          acc = item.selected ? acc.concat(new File([item.Path], item.Path, { type: item.MimeType })) : acc
        })
        return acc
      }, [])

      const errors = filesValidation(uploadedFiles.concat(selectedFromRepo), 'drop', schema, t, isOpenUploadModal)

      this.setState({
        selectedRepo: {
           Name: selectedRepo.Name,
           ID: selectedRepo.ID,
           Files: updatedFiles
         }
      })

      if (errors.length === 0) {
        this.setState({
          validationMessages: [],
          validationMessagesType: '',
          filesSelectedFromRepoForForm: selectedFromRepoToForm,
          filesSelectedFromRepo: selectedFromRepo
        })
      } else {
        this.setState({
          validationMessages: errors,
          validationMessagesType: 'custom'
        })
      }
    }
  }

  onClickFolder = (key) => () => {
    const { selectedRepo, breadcrumbData } = this.state

    const files = formatData(selectedRepo.Files[key], key)

    const repoToShow = {
      Name: selectedRepo.Name,
      ID: selectedRepo.ID,
      Files: files
    }

    this.setState({
      breadcrumbData: breadcrumbData.concat(capitalize(key)),
      selectedRepo: repoToShow,
      dataToFilter: repoToShow
    })
  }

  handleSelectFiles = () => {
    const {validationMessages, selectedRepo, filesSelectedFromRepo, uploadedFiles, filesSelectedFromRepoForForm} = this.state
    const { formContext, t } = this.props

    const { UStoreProvider } = formContext
    const { customState } = UStoreProvider.state.get()
    const { currentOrderItem, currentProductProperties } = customState

    if (validationMessages.length === 0) {
      const updatedFiles = selectedRepo && Object.keys(selectedRepo).length &&
        Object.keys(selectedRepo.Files).reduce((acc, key) => {
          const filesArray = selectedRepo.Files[key].map((file) => file.selected ? {...file, selected: false} : file)
          return { ...acc, [key]: filesArray }
        }, {})

      const existFiles = uploadedFiles.filter(({ name: id1 }) => filesSelectedFromRepo
        .some(({ name: id2 }) => id2 === id1));

      let combined
      const stringOfFilesForSend = filesSelectedFromRepoForForm.join(';')

      if(existFiles.length > 0) {
        const errors = existFiles.map((file) => t('ducs.file_already_exists', {FileName: file.name}))
        combined = uploadedFiles

        this.setState({
          validationMessages: errors
        })
      } else {
        const names = new Set(filesSelectedFromRepo.map(({ name }) => name))
        combined = [...uploadedFiles.filter(({ name }) => !names.has(name)), ...filesSelectedFromRepo]
      }

      const updatedFormData = Object.keys(currentProductProperties.formData).reduce((acc, next) =>
        ({...acc, [next]: next === this.props.id ? stringOfFilesForSend : ''}), {})

      this.setState({
        selectedRepo: {
          Name: selectedRepo.Name,
          ID: selectedRepo.ID,
          Files: updatedFiles
        },
        uploadedFiles: combined
      })

      UStoreProvider.api.orders.updateProperties(
        currentOrderItem.ID,
        convertProductPropertiesFormIntoArray({...currentProductProperties, formData: updatedFormData, errors: null})
      ).then((res) => {
        if(res) {
          this.props.onChange(stringOfFilesForSend)
        }
      })
      this.handleCloseUploadModal()
    }
  }

  clearValidationMessages = () => {
    const isClear = this.state.validationMessagesType === 'PreflightFailed' ? !!this.state.downloadedPreflight : true

    if(isClear) {
      this.setState({
        validationMessages: [],
        validationMessagesType: '',
        downloadedPreflight: ''
      })
    }
  }

  updateParentState = (name, param) => {this.setState({[name]: param})}

  confirmationOfUpload = (pg) => () => {this.setState({downloadedPreflight: pg})}

  render(){
    const { options = {}, disabled = false, t } = this.props
    const {isOpenUploadModal, repoFiles, filteredRepos, breadcrumbData, selectedRepo, dataToFilter,
      validationMessages, validationMessagesType, uploadedFiles, isOpenConfirm, uploadFilesInProgress} = this.state
    const fileName = this.fileNameArr[this.fileNameArr.length - 1]

    return(
      <div className={`file-upload-duc ${validationMessages.length > 0 ? 'duc-custom-error' : ''}`}>
        <div className='upload-control'>
          {options.custom.templateUrl && <div className="download-wrapper-link">
            <Icon
              name='download.svg'
              width="14px"
              height="14px"
              classes="download-icon"
            />
            <a
              onClick={this.confirmationOfUpload(options.custom.templateUrl)}
              download={fileName}
              href={options.custom.templateUrl}
            >{t('ducs.download_template')}</a>
          </div>
          }
          <ClickListener trigger={this.clearValidationMessages}>
            <>
              {isOpenUploadModal &&
                <UploadModal
                  repoFiles={repoFiles}
                  filteredRepos={filteredRepos}
                  breadcrumbData={breadcrumbData}
                  selectedRepo={selectedRepo}
                  dataToFilter={dataToFilter}
                  validationMessagesType={validationMessagesType}
                  validationMessages={validationMessages}
                  supportedFiles={options.custom.allowedExtensions}
                  handleCloseModal={this.handleCloseUploadModal}
                  selectRepo={this.selectRepo}
                  onSelectFile={this.onSelectFile}
                  onClickFolder={this.onClickFolder}
                  handleSelectFiles={this.handleSelectFiles}
                  updateParentState={this.updateParentState}
                  t={t}
                  title={t('ducs.repository')}
                  cancelText={t('ducs.cancel')}
                  acceptText={t('ducs.select')}
                />
              }
              <Dropzone noClick noKeyboard onDrop={(acceptedFiles) => this.onDrop(acceptedFiles)}>
                {(dropProps) => {
                  const { getRootProps, getInputProps, isDragActive } = dropProps
                  const rootProps = getRootProps()

                  return (
                    <div className={`${isDragActive ? 'bgGrey' : ''} ${this.fileSupport && this.fileSupport.length <= 0 ? 'marginB20' : ''}`}>
                      <div
                        {...getRootProps({
                          className: `${disabled ? 'disabled' : ''} upload-area-wrapper ${this.isRepo ? 'centered' : ''}`
                        })}
                      >
                        {!this.isRepo && <span className="drag-title">{t('ducs.drag_and_drop_here')} {t('ducs.or')} </span>}
                        {this.isLocal && <div className="upload-button" tabIndex="0" data-xmpie-focusable="true"
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.keyCode === 13) {
                                                  e.target.querySelector('.upload-input').click()
                                                }
                                              }}
                        >
                          <input
                            ref={this.uploadRef}
                            {...getInputProps({
                              onClick: (e) =>{e.target.value = ''},
                              onChange: this.validate(rootProps.onDrop, uploadedFiles, 'local'),
                              className: 'upload-input'
                            })}
                          />
                          <Icon name="upload.svg" width="14px" height="14px" className="upload-icon"/>
                          <span>{t('ducs.upload_file')}</span>
                        </div>
                        }
                        {this.isRepo && this.isLocal && <div className='buttons-splitter'> {t('ducs.or')} </div>}
                        {this.isRepo && <div
                          className="upload-button repo-button"
                          onClick={this.validate(this.getRpoFiles, uploadedFiles, 'repo')}
                          tabIndex='0'
                        >
                          <Icon name="file.svg" width="14px" height="14px" className="file-icon"/>
                          <span>{t('ducs.select_from_repo')}</span>
                        </div>
                        }
                      </div>
                    </div>
                  )
                }}
              </Dropzone>
            </>
          </ClickListener>
          {this.fileSupport && this.fileSupport.length > 0
            && <span className='file-type-label'>{`${t('ducs.supported_files')}: ${this.fileSupport}`}</span>
          }
          {validationMessages.map((msg, index) => {
            if(validationMessagesType === 'PreflightFailed') {
              const re_ = /<a(.*)?>(.*)?<\/a>/
              const link = msg.replace(re_, '<a $1 download="preflight" id="preflightLink">$2</a>')

              return <div className={'duc-error'} dangerouslySetInnerHTML={{ __html: link}} />
            } else {
              return <div key={index} className={'duc-error'}>{msg}</div>
            }
          })}
        </div>
        <div className={`uploaded-files ${!uploadedFiles || uploadedFiles.length === 0 ? 'empty-files-list' : ''}`}>
          {uploadedFiles && uploadedFiles.length > 0 && uploadedFiles.map((file, index) => {
              return (
                file && <div className='current-file' key={`${file.Path}_${index}`}>
                  <div className="current-file-name">
                    <span className='delete-file' onClick={this.onDeleteFile(file.FileName || file.name)}>
                      <Icon
                        name='remove.svg'
                        width="10px"
                        height="10px"
                        classes="folder-icon"
                      />
                    </span>
                    <span className="file-name">{file.FileName || file.name}</span>
                  </div>
                </div>
              )})
          }
          {uploadFilesInProgress && uploadFilesInProgress.length > 0 && uploadFilesInProgress.map((file, index) => {
              return (
                file && <div className='current-file' key={`${file.Path}_${index}`}>
                  <div className="current-file-name">
                    <span className="file-name">
                      {file.FileName || file.name}
                      <span className='uploading-progress'>{t('ducs.uploading')} </span>
                      <LoadingDucsDots />
                    </span>
                  </div>
                </div>
              )})
          }
        </div>
        {isOpenConfirm && <ConfirmModal
          handleCloseModal={this.handleCloseConfirmModal}
          handleConfirmModal={this.handleConfirmModal}
          title={t('ducs.confirm_deletion')}
          cancelText={t('ducs.cancel')}
          confirm={t('ducs.ok')}
          text={t('ducs.sure_to_remove_file')}
          t={t}
        />}
      </div>
    )
  }
}

if (typeof window !== 'undefined') {
  window.uStoreDucs = window.uStoreDucs || []
  window.uStoreDucs.push({ name: 'fileUpload', component: FileUpload, displayName: 'File Attachment' })
}

export default FileUpload
export { formatType }

